import React from 'react';
import { PaginatedCardGrid } from 'core/components/PaginatedCardGrid';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { DigitalMarketingSearchInput } from 'features/DigitalMarketing/components/DigitalMarketingSearchInput';
import { ManagementFilterBar } from 'features/DigitalMarketing/components/ManagementFilterBar';
import { BlogCard } from '../../../components/BlogCard';
import type { BlogItem } from 'features/DigitalMarketing/slice/types';
import type { BlogPublishFilter } from '../blogUtils';
import {
  blogFilterOptions,
  isBlogPublished,
  toBlogCardModel,
} from '../blogUtils';
import { DMButton, DMDiv, DMText } from '../../../components/Primitives';

interface BlogDashboardViewProps {
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  onNewBlog: () => void;
  publishFilter: BlogPublishFilter;
  handleFilterChange: (filter: BlogPublishFilter) => void;
  isLoadingBlogs: boolean;
  blogs: BlogItem[];
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  handleEdit: (blog: BlogItem) => void;
  handlePreview: (blog: BlogItem) => void;
  onDelete: (id: number) => void;
  onPublish: (id: number) => void;
  onUnpublish: (id: number) => void;
}

export const BlogDashboardView: React.FC<BlogDashboardViewProps> = ({
  searchQuery,
  setSearchQuery,
  onNewBlog,
  publishFilter,
  handleFilterChange,
  isLoadingBlogs,
  blogs,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  handleEdit,
  handlePreview,
  onDelete,
  onPublish,
  onUnpublish,
}) => {
  const matchesSearch = (blog: BlogItem) => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return true;
    }

    return [
      blog.title,
      blog.TITLE,
      blog.excerpt,
      blog.EXCERPT,
      blog.content,
      blog.CONTENT,
    ]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(query));
  };

  const visibleBlogs = blogs
    .map(blog => ({ blog, isPublished: isBlogPublished(blog) }))
    .filter(({ isPublished }) => {
      if (publishFilter === 'published') {
        return isPublished;
      }

      if (publishFilter === 'saved') {
        return !isPublished;
      }

      return true;
    })
    .filter(({ blog }) => matchesSearch(blog));

  const emptyMessage =
    publishFilter === 'published'
      ? 'No published blogs found.'
      : publishFilter === 'saved'
      ? 'No saved blogs found.'
      : 'No blogs found.';

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white font-inter text-gray-950"
    >
      <DMDiv className="mb-[2.8vh] shrink-0">
        <DMDiv className="mb-[1.6vh] flex items-start justify-between gap-[2vw]">
          <DMDiv>
            <DigitalMarketingBreadcrumb
              className="mb-[1.2vh]"
              items={[{ label: 'Blogs' }]}
            />
            <DMText
              as="h1"
              className="text-[3.5vh] font-bold leading-tight text-gray-950"
            >
              Blogs
            </DMText>
            <DMText className="mt-[0.65vh] text-[1.65vh] font-medium leading-tight text-gray-600">
              Create Impactful blogs to strengthen your school's brand
            </DMText>
          </DMDiv>

          <DMDiv className="flex shrink-0 items-center gap-[0.8vw] pt-[4.8vh]">
            <DigitalMarketingSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search Blogs"
            />
            <DMButton
              type="button"
              onClick={onNewBlog}
              className="flex h-[6.4vh] items-center gap-[0.8vw] rounded-full bg-primary-main px-[2vw] text-[1.7vh] font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
            >
              <DMText as="span" className="text-[2.4vh] leading-none">
                +
              </DMText>
              <DMText as="span">Create New Blog</DMText>
            </DMButton>
          </DMDiv>
        </DMDiv>
      </DMDiv>

      <PaginatedCardGrid
        emptyMessage={emptyMessage}
        filterSlot={
          <ManagementFilterBar
            className="mb-[2.2vh] shrink-0"
            options={blogFilterOptions}
            selectedValue={publishFilter}
            allValue="both"
            onChange={handleFilterChange}
          />
        }
        hasItems={visibleBlogs.length > 0}
        isLoading={isLoadingBlogs}
        itemLabel="blog"
        itemLabelPlural="blogs"
        pageIndex={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 30, 40, 50]}
        totalItems={totalCount}
        onPageIndexChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        renderSkeleton={index => (
          <DMDiv
            key={index}
            className="min-h-[240px] animate-pulse rounded-2xl bg-gray-100"
          />
        )}
      >
        {visibleBlogs.map(({ blog, isPublished }) => {
          const { blogId, blog: cardBlog } = toBlogCardModel(blog);
          const published = isPublished || isBlogPublished(cardBlog);
          const canDelete = !!blogId && !published;

          return (
            <BlogCard
              key={`${published ? 'published' : 'saved'}-${
                blogId ??
                `${cardBlog.title}-${
                  cardBlog.date || cardBlog.created_at || ''
                }`
              }`}
              blog={cardBlog}
              isPublished={published}
              onEdit={() => handleEdit(blog)}
              onPreview={() => handlePreview(blog)}
              onDelete={() => {
                if (canDelete) {
                  onDelete(blogId);
                }
              }}
              onPublish={
                !published ? () => blogId && onPublish(blogId) : undefined
              }
              onUnpublish={
                published ? () => blogId && onUnpublish(blogId) : undefined
              }
            />
          );
        })}
      </PaginatedCardGrid>
    </DMDiv>
  );
};
